import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Phone, Mail, Award } from 'lucide-react';
import { FaFacebookF, FaInstagram, FaYoutube, FaLinkedinIn } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

import { newsDetailService } from '../../services/otherService';
import logoImg from '../../assets/img/logo.svg';
import appStore from '../../assets/img/app-store.svg';
import googlePlay from '../../assets/img/play-store.svg';

const BRAND = '#842014';
const FOOTER_BG = '#7A1717';

const s = {
  footer: {
    backgroundColor: FOOTER_BG,
    color: '#fff',
    paddingTop: '64px',
    paddingBottom: '32px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 24px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '48px',
    marginBottom: '64px',
  },
  brandDesc: {
    color: 'rgba(255,200,200,0.85)',
    fontSize: '0.875rem',
    lineHeight: 1.7,
    margin: '16px 0 20px',
  },
  contactLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '0.875rem',
    color: 'rgba(255,200,200,0.85)',
    textDecoration: 'none',
    marginBottom: '10px',
    transition: 'color 0.2s',
  },
  colHeading: {
    fontSize: '1.125rem',
    fontWeight: '700',
    marginBottom: '24px',
    color: '#fff',
  },
  colHeadingLink: {
    fontSize: '1.125rem',
    fontWeight: '700',
    marginBottom: '24px',
    color: '#fff',
    textDecoration: 'none',
    display: 'block',
  },
  linkList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  linkItem: {
    marginBottom: '12px',
  },
  footerLink: {
    fontSize: '0.875rem',
    color: 'rgba(255,200,200,0.85)',
    textDecoration: 'none',
    transition: 'color 0.2s',
    lineHeight: 1.5,
    display: 'block',
  },
  newsLink: {
    fontSize: '0.875rem',
    color: 'rgba(255,200,200,0.85)',
    textDecoration: 'none',
    transition: 'color 0.2s',
    lineHeight: 1.5,
    display: 'block',
    marginBottom: '16px',
  },
  // White app/rewards card
  appCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '48px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '32px',
    flexWrap: 'wrap',
  },
  appLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    minWidth: '240px',
    borderRight: '1px solid #e5e7eb',
    paddingRight: '32px',
  },
  appLogoText: {
    fontSize: '1.5rem',
    fontWeight: '800',
    letterSpacing: '-0.02em',
    color: '#111827',
    marginBottom: '8px',
  },
  appSubtext: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginBottom: '16px',
  },
  appButtons: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  appBtn: {
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background-color 0.2s',
  },
  appBtnImg: {
    width: '28px',
    height: '28px',
    objectFit: 'contain',
  },
  appBtnTextWrap: {
    textAlign: 'left',
  },
  appBtnSmall: {
    fontSize: '0.625rem',
    display: 'block',
    lineHeight: 1.2,
    marginBottom: '2px',
    color: 'rgba(255,255,255,0.8)',
  },
  appBtnBig: {
    fontSize: '0.875rem',
    fontWeight: '600',
    display: 'block',
    lineHeight: 1.2,
  },
  rewardsRight: {
    flex: 1,
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    paddingLeft: '32px',
  },
  rewardsHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '1.5rem',
    fontWeight: '700',
    color: BRAND,
    marginBottom: '8px',
  },
  rewardsSubtext: {
    color: '#6b7280',
    fontSize: '0.9375rem',
    margin: 0,
  },
  // Bottom bar
  bottomBar: {
    paddingTop: '32px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  copyright: {
    fontSize: '0.875rem',
    color: 'rgba(255,200,200,0.7)',
    margin: 0,
  },
  socialRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  socialBtn: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    textDecoration: 'none',
    transition: 'background-color 0.2s',
    border: 'none',
    cursor: 'pointer',
  },
};

const Footer = ({ isTeamsEZ }) => {
  const [newsList, setNewsList] = useState([]);

  useEffect(() => {
    newsDetailService('').then(res => {
      if (res?.data?.statusCode) {
        setNewsList(res.data.responseData.news);
      }
    });
  }, []);

  const brandName = isTeamsEZ ? 'TeamsEZ' : 'CampsEZ';
  const supportEmail = isTeamsEZ ? 'support@teamsez.com' : 'support@campsez.com';

  return (
    <footer style={s.footer}>
      <div style={s.container}>

        {/* 4-column grid */}
        <div style={s.grid}>

          {/* Brand */}
          <div>
            {isTeamsEZ ? (
              <span style={{ fontSize: '1.5rem', fontWeight: '800', color: '#fff', letterSpacing: '-0.02em' }}>
                {brandName}
              </span>
            ) : (
              <img
                src={logoImg}
                alt="CampsEZ"
                width={149}
                height={38}
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            )}
            <p style={s.brandDesc}>
              Reliable, secure, friendly service, every time. Allowing Families to register easily.
            </p>
            <a href="tel:+19712453061" style={s.contactLink}>
              <Phone size={16} /> +1 (971)-245-3061
            </a>
            <a href={`mailto:${supportEmail}`} style={s.contactLink}>
              <Mail size={16} /> {supportEmail}
            </a>
          </div>

          {/* About */}
          <div>
            <h3 style={s.colHeading}>About</h3>
            <ul style={s.linkList}>
              <li style={s.linkItem}><Link to="/about-us" style={s.footerLink}>About Us</Link></li>
              <li style={s.linkItem}><Link to="/career" style={s.footerLink}>Careers With Us</Link></li>
              <li style={s.linkItem}><Link to="/terms-and-conditions" style={s.footerLink}>Terms &amp; Conditions</Link></li>
              <li style={s.linkItem}><Link to="/privacy-policy" style={s.footerLink}>Privacy Policy</Link></li>
              <li style={s.linkItem}><Link to="/faq" style={s.footerLink}>Support &amp; FAQs</Link></li>
            </ul>
          </div>

          {/* Partner Services */}
          <div>
            <h3 style={s.colHeading}>Partner Services</h3>
            <ul style={s.linkList}>
              <li style={s.linkItem}><Link to="/list-camp" style={s.footerLink}>List Your Program</Link></li>
              <li style={s.linkItem}><Link to="/advertising" style={s.footerLink}>Advertising</Link></li>
              <li style={s.linkItem}><Link to="/partner" style={s.footerLink}>Become A Partner</Link></li>
            </ul>
          </div>

          {/* Recent News */}
          <div>
            <Link to="/news-and-events" style={s.colHeadingLink}>Recent News &amp; Events</Link>
            {newsList.slice(0, 3).map(item => (
              <a key={item._id} href={`/news-and-events/${item._id}`} style={s.newsLink}>
                - {item.title}
              </a>
            ))}
          </div>

        </div>

        {/* App Download & Rewards card */}
        <div style={s.appCard}>
          <div style={s.appLeft}>
            {isTeamsEZ ? (
              <span style={s.appLogoText}>
                Teams<span style={{ color: BRAND }}>EZ</span>
              </span>
            ) : (
              <img
                src={logoImg}
                alt="CampsEZ"
                width={149}
                height={38}
              />
            )}
            <p style={s.appSubtext}>Download the app and book on the go</p>
            <div style={s.appButtons}>
              <a
                href="https://apps.apple.com/us/app/campsez/id1521620775"
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-block', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <img src={appStore} alt="Download on the App Store" style={{ height: '44px', width: 'auto', display: 'block' }} />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.vic.campsez"
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-block', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <img src={googlePlay} alt="Get it on Google Play" style={{ height: '44px', width: 'auto', display: 'block' }} />
              </a>
            </div>
          </div>

          <div style={s.rewardsRight}>
            <div style={s.rewardsHeading}>
              <Award size={32} color={BRAND} />
              Rewards
            </div>
            <p style={s.rewardsSubtext}>Join now for our latest offers and special deals!</p>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={s.bottomBar}>
          <p style={s.copyright}>
            {brandName} © {new Date().getFullYear()} All Rights Reserved.
          </p>
          <div style={s.socialRow}>
            <a href="https://www.facebook.com/CampsEZ" target="_blank" rel="noreferrer" style={s.socialBtn}><FaFacebookF size={14} /></a>
            <a href="https://www.instagram.com/camps_ez/" target="_blank" rel="noreferrer" style={s.socialBtn}><FaInstagram size={14} /></a>
            <a href="https://www.youtube.com/channel/UCpe_I1IlZ5zyOSp7jnH7_3A" target="_blank" rel="noreferrer" style={s.socialBtn}><FaYoutube size={14} /></a>
            <a href="https://twitter.com/Camps_EZ" target="_blank" rel="noreferrer" style={s.socialBtn}><FaXTwitter size={14} /></a>
            <a href="https://www.linkedin.com/company/campsez/" target="_blank" rel="noreferrer" style={s.socialBtn}><FaLinkedinIn size={14} /></a>
          </div>
        </div>

      </div>

      <Helmet>
        <script>{`
          var AddShoppersWidgetOptions = { 'loadCss': false, 'pushResponse': false };
          (!function(){
            var t=document.createElement("script");
            t.type="text/javascript",t.async=!0,t.id="AddShoppers",
            t.src="https://shop.pe/widget/widget_async.js#5fc91b300ff4c31655eae406",
            document.getElementsByTagName("head")[0].appendChild(t)
          }());
        `}</script>
      </Helmet>
    </footer>
  );
};

const mapStateToProps = state => ({
  isTeamsEZ: state.ui.isTeamsEZ,
});

export default connect(mapStateToProps)(Footer);
